import { Box, Flex, HStack, Text, VStack } from "@chakra-ui/react";
import moment from "moment";
import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { CurrencyCodeEnum } from "src/api/generated/types";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";
import { D } from "src/utils/helpers";
import { FullServiceContext } from "./context";

interface QuarterData {
  quarter: number;
  totalCapGains: number;
  totalIncome: number;
  startMonth: string;
  start: Date;
  endMonth: string;
  end: Date;
}

export const Quarters = () => {
  const theme = useTheme();
  const { client, monthlyPoints } = useContext(FullServiceContext);

  const quarters = useMemo(() => {
    if (!monthlyPoints) return [];

    // Group monthly points into quarters
    const groupedByQuarter = monthlyPoints.reduce((acc, point) => {
      const date = moment.utc(point.timestamp);
      const quarter = Math.floor(date.month() / 3) + 1;

      if (!acc[quarter]) {
        acc[quarter] = {
          quarter,
          totalCapGains: 0,
          totalIncome: 0,
          startMonth: date.format("MMM"),
          start: date.toDate(),
          endMonth: moment()
            .month(date.month() + 2)
            .format("MMM"),
          end: moment()
            .month(date.month() + 2)
            .endOf("month")
            .toDate(),
        };
      }

      acc[quarter].totalCapGains += point.totalCapGainsCents;
      acc[quarter].totalIncome += point.netIncomeCents;

      return acc;
    }, {} as Record<number, QuarterData>);

    // Convert to array and sort by quarter
    return Object.values(groupedByQuarter).sort(
      (a, b) => a.quarter - b.quarter
    );
  }, [monthlyPoints]);

  return (
    <Box bg={theme.background} w="100%" mt="20px" mb="20px">
      <Flex gap={1} flexWrap="wrap">
        {quarters.map((quarter) => {
          const total = quarter.totalCapGains + quarter.totalIncome;
          const color = total === 0 ? theme.text : colors.black;

          return (
            <Link
              style={{ flex: 1 }}
              to={`/clients/${
                client?.id
              }/transactions?startDate=${quarter.start.toISOString()}&endDate=${quarter.end.toISOString()}`}
            >
              <Box
                key={quarter.quarter}
                flex="1"
                p="10px"
                border={`1px solid ${theme.border}`}
                borderRadius="lg"
                style={{
                  background:
                    quarter.totalCapGains === 0
                      ? theme.background
                      : quarter.totalCapGains >= 0
                      ? `linear-gradient(to bottom right, ${colors.positive}, ${colors.positive})`
                      : `linear-gradient(to bottom right, ${colors.negative}, ${colors.negative})`,
                }}
              >
                <HStack mb="10px">
                  <Text flex={1} fontWeight="bold" fontSize="md" color={color}>
                    Q{quarter.quarter} ({quarter.startMonth}-{quarter.endMonth})
                  </Text>

                  <i
                    className={
                      total >= 0
                        ? "fa-solid fa-chart-line"
                        : "fa-solid fa-chart-line-down"
                    }
                    style={{ fontSize: 16, color }}
                  />
                </HStack>
                <VStack mb="5px" w="100%" alignItems="flex-start">
                  <Box flex={1}>
                    <Text
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight="bold"
                      color={color}
                    >
                      Cap Gains
                    </Text>
                    <Flex alignItems="center">
                      <Text fontSize="lg" fontWeight="bold" color={color}>
                        {quarter.totalCapGains === 0
                          ? "-"
                          : quarter.totalCapGains < 0
                          ? `-${D(
                              Math.abs(quarter.totalCapGains),
                              client?.currency || CurrencyCodeEnum.Usd
                            ).toFormat()}`
                          : `+${D(
                              quarter.totalCapGains,
                              client?.currency || CurrencyCodeEnum.Usd
                            ).toFormat()}`}
                      </Text>
                    </Flex>
                  </Box>
                  <Box flex={1}>
                    <Text
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight="bold"
                      color={color}
                    >
                      Income
                    </Text>
                    <Flex ml="auto" alignItems="center">
                      <Text fontSize="lg" fontWeight="bold" color={color}>
                        {quarter.totalIncome === 0
                          ? "-"
                          : quarter.totalIncome < 0
                          ? `-${D(
                              Math.abs(quarter.totalIncome),
                              client?.currency || CurrencyCodeEnum.Usd
                            ).toFormat()}`
                          : `+${D(
                              quarter.totalIncome,
                              client?.currency || CurrencyCodeEnum.Usd
                            ).toFormat()}`}
                      </Text>
                    </Flex>
                  </Box>
                </VStack>
              </Box>
            </Link>
          );
        })}
      </Flex>
    </Box>
  );
};
